/**
 * ACTIVE ORGANIZATION switcher (piece 3 of MULTI_ORG_SWITCHER). Lists the
 * user's orgs and switches the active context (reactively re-scopes all
 * surfaces via the `lib/active-org.ts` module signal).
 *
 * ── Tenancy mode ──────────────────────────────────────────────────────────
 *
 * In "single academy" mode the selector doesn't render. **The single-academy
 * case was already covered** by the `orgs().length > 1` this component
 * had from the start: with one academy there was no selector, before or
 * now. What `canSwitchOrganization()` adds is the other half, the one that
 * `length` alone can't know — that the MODE forbids switching. The two conditions
 * are composed here; no logic is duplicated.
 *
 * And if the mode says "one academy" but the user sees more than one, that's not
 * hidden: the warning is rendered. A mode that lies about what's there is worse than
 * not having it. (The server shouts the same thing on boot — `core_rust::tenancy`.)
 */
import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { AlertTriangle, Building2 } from "@fleet/icons";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { activeTenancyMode, canSwitchOrganization, tenancyMismatch } from "@space/shared";
import { useActiveOrg } from "../hooks/use-active-org";

export function OrgSwitcher() {
  const { t } = useI18n();
  const { orgs, activeOrgId, setActiveOrg } = useActiveOrg();

  const mode = () => activeTenancyMode();
  const mismatch = () => tenancyMismatch(orgs().length, mode());

  const change = async (orgId: string) => {
    try {
      await setActiveOrg(orgId);
    } catch (err) {
      console.error("Error switching organization:", err);
      showToast(t(msg`Error switching organization`), "error");
    }
  };

  return (
    <>
      <Show when={mismatch()}>
        {(m) => (
          <div
            data-testid="tenancy-mismatch"
            role="alert"
            class="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {(() => {
                const { actual } = m();
                const declared = m().declaredMax;
                return t(
                  msg`This deployment is configured for ${declared} academy, but ${actual} are visible. Report it to the administrator.`,
                );
              })()}
            </span>
          </div>
        )}
      </Show>
      <Show when={canSwitchOrganization(mode()) && orgs().length > 1}>
        <div data-testid="org-switcher">
          <Select
            aria-label={t(msg`Active organization`)}
            icon={<Building2 class="h-4 w-4" />}
            value={activeOrgId()}
            onChange={change}
            triggerClass="w-56"
            options={orgs().map((o) => ({ value: o.id, label: o.name ?? o.slug ?? o.id }))}
          />
        </div>
      </Show>
    </>
  );
}
